import React, { useState } from 'react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@roger/ui-components';
import { Button } from '@roger/ui-components';
import { Input } from '@roger/ui-components';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@roger/ui-components';
import { Textarea } from '@roger/ui-components';
import { useToast } from '../hooks/useToastContext';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@roger/ui-components';

// Import generated types and API function
import { checkEligibilityStediEligibilityPost } from '../services/api/sdk.gen';
import type {
  StediEligibilityRequest,
  StediEligibilityResponse,
  StediEligibilityProvider,
  StediEligibilitySubscriber,
  StediAddress,
} from '../services/api/types.gen';
import { AxiosError } from 'axios';
import { NpiInput } from './NpiInput';

// Common test payers from Stedi's network
const stediPayerOptions = [
  { value: '60054', label: 'Aetna' },
  { value: '040', label: 'Anthem Blue Cross Blue Shield of CA' },
  { value: '62308', label: 'Cigna' },
  { value: '87726', label: 'UnitedHealthcare' },
];

const serviceTypeOptions = [
  { value: '30', label: '(30) Health Benefit Plan Coverage' },
  { value: '1', label: '(1) Medical Care' },
  { value: '33', label: '(33) Chiropractic' },
  { value: '35', label: '(35) Dental Care' },
  { value: '47', label: '(47) Hospital' },
  { value: '86', label: '(86) Emergency Services' },
  { value: '88', label: '(88) Pharmacy' },
  { value: '98', label: '(98) Professional (Physician) Visit - Office' },
];

const productOrServiceIDQualifierOptions = [
  { value: 'AD', label: 'AD - American Dental Association Codes' },
  { value: 'CJ', label: 'CJ - Current Procedural Terminology (CPT) Codes' },
  { value: 'HC', label: 'HC - HCPCS Codes' },
  { value: 'ID', label: 'ID - ICD-9-CM Procedure' },
  { value: 'IV', label: 'IV - HIEC Product/Service Code' },
  { value: 'N4', label: 'N4 - National Drug Code' },
  { value: 'ZZ', label: 'ZZ - Mutually Defined' },
];

const referenceIdentificationQualifierOptions = [
  { value: '9F', label: '9F - Referral Number' },
  { value: 'G1', label: 'G1 - Prior Authorization Number' },
];

const diagnosisTypeCodeOptions = [
  { value: 'ABK', label: 'ABK - ICD-10-CM Principal Diagnosis' },
  { value: 'ABF', label: 'ABF - ICD-10-CM Diagnosis' },
  { value: 'BK', label: 'BK - ICD-9-CM Principal Diagnosis' },
  { value: 'BF', label: 'BF - ICD-9-CM Diagnosis' },
];

const individualRelationshipCodeOptions = [
  { value: '01', label: '01 - Spouse' },
  { value: '19', label: '19 - Child' },
  { value: '34', label: '34 - Other Adult' },
];

const providerCodeOptions = [
  { value: 'AD', label: 'AD - Admitting' },
  { value: 'AT', label: 'AT - Attending' },
  { value: 'BI', label: 'BI - Billing' },
  { value: 'CO', label: 'CO - Consulting' },
  { value: 'CV', label: 'CV - Covering' },
  { value: 'H', label: 'H - Hospital' },
  { value: 'HH', label: 'HH - Home Health' },
  { value: 'LA', label: 'LA - Laboratory' },
  { value: 'OT', label: 'OT - Other' },
  { value: 'P1', label: 'P1 - Pharmacist' },
  { value: 'P2', label: 'P2 - Pharmacy' },
  { value: 'PC', label: 'PC - Primary Care Provider' },
  { value: 'PE', label: 'PE - Performing' },
  { value: 'R', label: 'R - Referring' },
  { value: 'RF', label: 'RF - Referring Provider' },
  { value: 'SB', label: 'SB - Submitting' },
  { value: 'SK', label: 'SK - Skilled Nursing Facility' },
  { value: 'SU', label: 'SU - Supervising' },
];

const providerTypeOptions = [
  { value: 'employer', label: 'Employer' },
  { value: 'facility', label: 'Facility' },
  { value: 'gateway provider', label: 'Gateway Provider' },
  { value: 'hospital', label: 'Hospital' },
  { value: 'payer', label: 'Payor' },
  { value: 'plan sponsor', label: 'Plan Sponsor' },
  { value: 'provider', label: 'Provider' },
  { value: 'third-party administrator', label: 'Third-Party Administrator' },
];

const subscriberReferenceIdQualifierOptions = [
  { value: '9K', label: '9K - Servicer' },
  {
    value: 'D3',
    label: 'D3 - National Association of Boards of Pharmacy Number',
  },
  { value: 'EI', label: 'EI - Employer Identification Number' },
  {
    value: 'HPI',
    label:
      'HPI - Centers for Medicare and Medicaid Services National Provider Identifier',
  },
  {
    value: 'PXC',
    label: 'PXC - Health Care Financing Administration National Provider ID',
  },
  { value: 'SY', label: 'SY - Social Security Number' },
  { value: 'TJ', label: 'TJ - Federal Taxpayer Identification Number' },
];

const Stedi_270_Testing: React.FC = () => {
  const { success, error } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [response, setResponse] = useState<StediEligibilityResponse | null>(
    null
  );
  const [activeTab, setActiveTab] = useState('request');

  const [formData, setFormData] = useState<StediEligibilityRequest>({
    tradingPartnerServiceId: '60054',
    controlNumber: '123456789',
    provider: {
      organizationName: 'Blossom Ridge Health',
      npi: '1417441361',
      providerCode: 'PC',
      referenceIdentification: '207Q00000X',
      address: {
        address1: '123 Medical Center Dr',
        address2: 'Suite 100',
        city: 'Dallas',
        state: 'TX',
        postalCode: '75201',
      },
    },
    subscriber: {
      memberId: 'AETNA9wcSu',
      firstName: 'John',
      lastName: 'Doe',
    },
    dependents: [
      {
        firstName: 'Jordan',
        lastName: 'Doe',
        dateOfBirth: '20010714',
      },
    ],
    encounter: {
      serviceTypeCodes: ['30'],
      dateOfService: '20240315',
    },
  });

  const handleProviderChange = (
    field: keyof StediEligibilityProvider,
    value: string
  ) => {
    setFormData(prev => ({
      ...prev,
      provider: {
        ...prev.provider,
        [field]: value,
      },
    }));
  };

  const handleSubscriberChange = (
    field: keyof StediEligibilitySubscriber,
    value: string
  ) => {
    setFormData(prev => ({
      ...prev,
      subscriber: {
        ...prev.subscriber,
        [field]: field === 'dateOfBirth' ? value.replace(/-/g, '') : value,
      },
    }));
  };

  const handleServiceTypesChange = (values: string[]) => {
    setFormData(prev => ({
      ...prev,
      encounter: {
        ...prev.encounter,
        serviceTypeCodes: values,
      },
    }));
  };

  const handleSubmit = async () => {
    setIsLoading(true);
    setResponse(null);
    try {
      // Validate required fields
      if (!formData.tradingPartnerServiceId) {
        throw new Error('Please select a payor');
      }

      // Clean up the request - remove empty arrays and optional fields
      const cleanedFormData: StediEligibilityRequest = {
        tradingPartnerServiceId: formData.tradingPartnerServiceId,
        controlNumber: formData.controlNumber,
        provider: formData.provider,
        subscriber: formData.subscriber,
        encounter: formData.encounter,
        ...(formData.dependents && formData.dependents.length > 0
          ? { dependents: formData.dependents }
          : {}),
      };

      // Use the generated API function
      const result = await checkEligibilityStediEligibilityPost({
        body: cleanedFormData,
      });

      if (result.data) {
        setResponse(result.data);
        setActiveTab('response'); // Switch to response tab
      }
      success('Stedi eligibility check completed successfully!');
    } catch (err: unknown) {
      console.error('Error:', err);
      if (err instanceof Error) {
        const errorMessage = err?.message || 'Failed to check eligibility';
        error(errorMessage);
      } else if (err instanceof AxiosError) {
        const errorMessage =
          err?.response?.data?.detail ||
          err?.message ||
          'Failed to check eligibility';
        error(errorMessage);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const formatDateForDisplay = (dateStr: string) => {
    if (!dateStr || dateStr.length !== 8) return dateStr;
    return `${dateStr.slice(0, 4)}-${dateStr.slice(4, 6)}-${dateStr.slice(6, 8)}`;
  };

  return (
    <div className='space-y-6'>
      <Card>
        <CardHeader>
          <CardTitle>Stedi 270 Eligibility Testing</CardTitle>
          <CardDescription>
            Test eligibility checks using Stedi's JSON API format
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className='grid w-full grid-cols-2'>
              <TabsTrigger value='request'>Request</TabsTrigger>
              <TabsTrigger value='response'>Response</TabsTrigger>
            </TabsList>

            <TabsContent value='request' className='space-y-6'>
              {/* Provider Information */}
              <div className='space-y-4'>
                <h3 className='text-lg font-semibold'>Provider Information</h3>
                <div className='grid grid-cols-2 gap-4'>
                  <div>
                    <label
                      htmlFor='organizationName'
                      className='block text-sm font-medium mb-2'
                    >
                      Organization Name
                    </label>
                    <Input
                      id='organizationName'
                      value={formData.provider.organizationName || ''}
                      onChange={e =>
                        handleProviderChange('organizationName', e.target.value)
                      }
                      placeholder='Healthcare Provider Name'
                    />
                  </div>
                  <div>
                    <label
                      htmlFor='npi'
                      className='block text-sm font-medium mb-2'
                    >
                      NPI (Optional)
                    </label>
                    <NpiInput
                      id='npi'
                      value={formData.provider.npi || ''}
                      onValueChange={npi => handleProviderChange('npi', npi)}
                      placeholder='1234567890'
                    />
                  </div>
                  <div>
                    <label
                      htmlFor='taxId'
                      className='block text-sm font-medium mb-2'
                    >
                      Tax ID (Optional)
                    </label>
                    <Input
                      id='taxId'
                      value={formData.provider.taxId || ''}
                      onChange={e =>
                        handleProviderChange('taxId', e.target.value)
                      }
                      placeholder='123456789'
                      maxLength={9}
                    />
                  </div>
                  <div>
                    <label
                      htmlFor='referenceIdentification'
                      className='block text-sm font-medium mb-2'
                    >
                      Taxonomy Code (Optional)
                    </label>
                    <Input
                      id='referenceIdentification'
                      value={formData.provider.referenceIdentification || ''}
                      onChange={e =>
                        handleProviderChange(
                          'referenceIdentification',
                          e.target.value
                        )
                      }
                      placeholder='207Q00000X'
                    />
                  </div>
                </div>

                {/* Additional Provider IDs (collapsed by default) */}
                <details className='mt-4'>
                  <summary className='cursor-pointer text-sm font-medium text-muted-foreground'>
                    Additional Provider Identifiers (Optional)
                  </summary>
                  <div className='grid grid-cols-2 gap-4 mt-4'>
                    <div>
                      <label
                        htmlFor='stateLicenseNumber'
                        className='block text-sm font-medium mb-2'
                      >
                        State License Number
                      </label>
                      <Input
                        id='stateLicenseNumber'
                        value={formData.provider.stateLicenseNumber || ''}
                        onChange={e =>
                          handleProviderChange(
                            'stateLicenseNumber',
                            e.target.value
                          )
                        }
                        placeholder='State license #'
                      />
                    </div>
                    <div>
                      <label
                        htmlFor='medicareProviderNumber'
                        className='block text-sm font-medium mb-2'
                      >
                        Medicare Provider Number
                      </label>
                      <Input
                        id='medicareProviderNumber'
                        value={formData.provider.medicareProviderNumber || ''}
                        onChange={e =>
                          handleProviderChange(
                            'medicareProviderNumber',
                            e.target.value
                          )
                        }
                        placeholder='Medicare provider #'
                      />
                    </div>
                    <div>
                      <label
                        htmlFor='medicaidProviderNumber'
                        className='block text-sm font-medium mb-2'
                      >
                        Medicaid Provider Number
                      </label>
                      <Input
                        id='medicaidProviderNumber'
                        value={formData.provider.medicaidProviderNumber || ''}
                        onChange={e =>
                          handleProviderChange(
                            'medicaidProviderNumber',
                            e.target.value
                          )
                        }
                        placeholder='Medicaid provider #'
                      />
                    </div>
                    <div>
                      <label
                        htmlFor='submitterIdNumber'
                        className='block text-sm font-medium mb-2'
                      >
                        Submitter ID Number
                      </label>
                      <Input
                        id='submitterIdNumber'
                        value={formData.provider.submitterIdNumber || ''}
                        onChange={e =>
                          handleProviderChange(
                            'submitterIdNumber',
                            e.target.value
                          )
                        }
                        placeholder='Submitter ID'
                      />
                    </div>
                    <div>
                      <label
                        htmlFor='providerCode'
                        className='block text-sm font-medium mb-2'
                      >
                        Provider Code
                      </label>
                      <Select
                        value={formData.provider.providerCode || ''}
                        onValueChange={value =>
                          handleProviderChange('providerCode', value)
                        }
                      >
                        <SelectTrigger>
                          <SelectValue placeholder='Select provider code' />
                        </SelectTrigger>
                        <SelectContent>
                          {providerCodeOptions.map(option => (
                            <SelectItem key={option.value} value={option.value}>
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label
                        htmlFor='providerType'
                        className='block text-sm font-medium mb-2'
                      >
                        Provider Type
                      </label>
                      <Select
                        value={formData.provider.providerType || ''}
                        onValueChange={value =>
                          handleProviderChange('providerType', value)
                        }
                      >
                        <SelectTrigger>
                          <SelectValue placeholder='Select provider type' />
                        </SelectTrigger>
                        <SelectContent>
                          {providerTypeOptions.map(option => (
                            <SelectItem key={option.value} value={option.value}>
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </details>
              </div>

              {/* Subscriber Information */}
              <div className='space-y-4'>
                <h3 className='text-lg font-semibold'>
                  Subscriber Information
                </h3>
                <div className='grid grid-cols-2 gap-4'>
                  <div>
                    <label
                      htmlFor='memberId'
                      className='block text-sm font-medium mb-2'
                    >
                      Member ID
                    </label>
                    <Input
                      id='memberId'
                      value={formData.subscriber.memberId || ''}
                      onChange={e =>
                        handleSubscriberChange('memberId', e.target.value)
                      }
                      placeholder='123456789'
                    />
                  </div>
                  <div>
                    <label
                      htmlFor='gender'
                      className='block text-sm font-medium mb-2'
                    >
                      Gender
                    </label>
                    <Select
                      value={formData.subscriber.gender || ''}
                      onValueChange={(value: 'M' | 'F') =>
                        handleSubscriberChange('gender', value)
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value='M'>Male</SelectItem>
                        <SelectItem value='F'>Female</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label
                      htmlFor='firstName'
                      className='block text-sm font-medium mb-2'
                    >
                      First Name
                    </label>
                    <Input
                      id='firstName'
                      value={formData.subscriber.firstName || ''}
                      onChange={e =>
                        handleSubscriberChange('firstName', e.target.value)
                      }
                      placeholder='John'
                    />
                  </div>
                  <div>
                    <label
                      htmlFor='lastName'
                      className='block text-sm font-medium mb-2'
                    >
                      Last Name
                    </label>
                    <Input
                      id='lastName'
                      value={formData.subscriber.lastName || ''}
                      onChange={e =>
                        handleSubscriberChange('lastName', e.target.value)
                      }
                      placeholder='Doe'
                    />
                  </div>
                  <div>
                    <label
                      htmlFor='dateOfBirth'
                      className='block text-sm font-medium mb-2'
                    >
                      Date of Birth
                    </label>
                    <Input
                      id='dateOfBirth'
                      type='date'
                      value={formatDateForDisplay(
                        formData.subscriber.dateOfBirth || ''
                      )}
                      onChange={e =>
                        handleSubscriberChange('dateOfBirth', e.target.value)
                      }
                    />
                  </div>
                  <div>
                    <label
                      htmlFor='ssn'
                      className='block text-sm font-medium mb-2'
                    >
                      SSN (Optional)
                    </label>
                    <Input
                      id='ssn'
                      value={formData.subscriber.ssn || ''}
                      onChange={e =>
                        handleSubscriberChange('ssn', e.target.value)
                      }
                      placeholder='123456789'
                      maxLength={9}
                    />
                  </div>
                  <div>
                    <label
                      htmlFor='middleName'
                      className='block text-sm font-medium mb-2'
                    >
                      Middle Name (Optional)
                    </label>
                    <Input
                      id='middleName'
                      value={formData.subscriber.middleName || ''}
                      onChange={e =>
                        handleSubscriberChange('middleName', e.target.value)
                      }
                      placeholder='M'
                    />
                  </div>
                  <div>
                    <label
                      htmlFor='suffix'
                      className='block text-sm font-medium mb-2'
                    >
                      Suffix (Optional)
                    </label>
                    <Input
                      id='suffix'
                      value={formData.subscriber.suffix || ''}
                      onChange={e =>
                        handleSubscriberChange('suffix', e.target.value)
                      }
                      placeholder='Jr.'
                    />
                  </div>
                  <div>
                    <label
                      htmlFor='groupNumber'
                      className='block text-sm font-medium mb-2'
                    >
                      Group Number (Optional)
                    </label>
                    <Input
                      id='groupNumber'
                      value={formData.subscriber.groupNumber || ''}
                      onChange={e =>
                        handleSubscriberChange('groupNumber', e.target.value)
                      }
                      placeholder='GRP123456'
                    />
                  </div>
                </div>

                {/* Additional Subscriber Fields (collapsed by default) */}
                <details className='mt-4'>
                  <summary className='cursor-pointer text-sm font-medium text-muted-foreground'>
                    Additional Subscriber Information (Optional)
                  </summary>
                  <div className='grid grid-cols-2 gap-4 mt-4'>
                    <div>
                      <label
                        htmlFor='medicaidRecipientId'
                        className='block text-sm font-medium mb-2'
                      >
                        Medicaid Recipient ID
                      </label>
                      <Input
                        id='medicaidRecipientId'
                        value={
                          formData.subscriber
                            .medicaidRecipientIdentificationNumber || ''
                        }
                        onChange={e =>
                          handleSubscriberChange(
                            'medicaidRecipientIdentificationNumber',
                            e.target.value
                          )
                        }
                        placeholder='Medicaid ID'
                      />
                    </div>
                    <div>
                      <label
                        htmlFor='caseNumber'
                        className='block text-sm font-medium mb-2'
                      >
                        Case Number
                      </label>
                      <Input
                        id='caseNumber'
                        value={formData.subscriber.caseNumber || ''}
                        onChange={e =>
                          handleSubscriberChange('caseNumber', e.target.value)
                        }
                        placeholder='Case #'
                      />
                    </div>
                    <div>
                      <label
                        htmlFor='idCard'
                        className='block text-sm font-medium mb-2'
                      >
                        ID Card Number
                      </label>
                      <Input
                        id='idCard'
                        value={formData.subscriber.idCard || ''}
                        onChange={e =>
                          handleSubscriberChange('idCard', e.target.value)
                        }
                        placeholder='ID card #'
                      />
                    </div>
                    <div>
                      <label
                        htmlFor='birthSequenceNumber'
                        className='block text-sm font-medium mb-2'
                      >
                        Birth Sequence Number
                      </label>
                      <Input
                        id='birthSequenceNumber'
                        value={formData.subscriber.birthSequenceNumber || ''}
                        onChange={e =>
                          handleSubscriberChange(
                            'birthSequenceNumber',
                            e.target.value
                          )
                        }
                        placeholder='For multiple births'
                      />
                    </div>
                  </div>

                  {/* Subscriber Address */}
                  <div className='mt-4'>
                    <h5 className='text-sm font-medium mb-2'>
                      Subscriber Address (Optional)
                    </h5>
                  </div>

                  {/* Spend Down Fields */}
                  <div className='mt-4'>
                    <h5 className='text-sm font-medium mb-2'>
                      Spend Down Information (Optional)
                    </h5>
                    <div className='grid grid-cols-2 gap-4'>
                      <div>
                        <label
                          htmlFor='spendDownAmount'
                          className='block text-sm font-medium mb-2'
                        >
                          Spend Down Amount
                        </label>
                        <Input
                          id='spendDownAmount'
                          value={formData.subscriber.spendDownAmount || ''}
                          onChange={e =>
                            handleSubscriberChange(
                              'spendDownAmount',
                              e.target.value
                            )
                          }
                          placeholder='500.00'
                        />
                      </div>
                      <div>
                        <label
                          htmlFor='spendDownTotalBilledAmount'
                          className='block text-sm font-medium mb-2'
                        >
                          Spend Down Total Billed Amount
                        </label>
                        <Input
                          id='spendDownTotalBilledAmount'
                          value={
                            formData.subscriber.spendDownTotalBilledAmount || ''
                          }
                          onChange={e =>
                            handleSubscriberChange(
                              'spendDownTotalBilledAmount',
                              e.target.value
                            )
                          }
                          placeholder='1500.00'
                        />
                      </div>
                    </div>
                  </div>

                  {/* Provider Reference Fields */}
                  <div className='mt-4'>
                    <h5 className='text-sm font-medium mb-2'>
                      Provider Reference (Optional)
                    </h5>
                    <div className='grid grid-cols-2 gap-4'>
                      <div>
                        <label
                          htmlFor='subscriberProviderCode'
                          className='block text-sm font-medium mb-2'
                        >
                          Provider Code
                        </label>
                        <Select
                          value={formData.subscriber.providerCode || ''}
                          onValueChange={value =>
                            handleSubscriberChange('providerCode', value)
                          }
                        >
                          <SelectTrigger>
                            <SelectValue placeholder='Select provider code' />
                          </SelectTrigger>
                          <SelectContent>
                            {providerCodeOptions.map(option => (
                              <SelectItem
                                key={option.value}
                                value={option.value}
                              >
                                {option.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <label
                          htmlFor='subscriberRefIdQualifier'
                          className='block text-sm font-medium mb-2'
                        >
                          Reference ID Qualifier
                        </label>
                        <Select
                          value={
                            formData.subscriber
                              .referenceIdentificationQualifier || ''
                          }
                          onValueChange={value =>
                            handleSubscriberChange(
                              'referenceIdentificationQualifier',
                              value
                            )
                          }
                        >
                          <SelectTrigger>
                            <SelectValue placeholder='Select reference ID qualifier' />
                          </SelectTrigger>
                          <SelectContent>
                            {subscriberReferenceIdQualifierOptions.map(
                              option => (
                                <SelectItem
                                  key={option.value}
                                  value={option.value}
                                >
                                  {option.label}
                                </SelectItem>
                              )
                            )}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <label
                          htmlFor='providerIdentifier'
                          className='block text-sm font-medium mb-2'
                        >
                          Provider Identifier
                        </label>
                        <Input
                          id='providerIdentifier'
                          value={formData.subscriber.providerIdentifier || ''}
                          onChange={e =>
                            handleSubscriberChange(
                              'providerIdentifier',
                              e.target.value
                            )
                          }
                          placeholder='Provider ID'
                        />
                      </div>
                    </div>
                  </div>

                  {/* Diagnosis Codes */}
                  <div className='mt-4'>
                    <h5 className='text-sm font-medium mb-2'>
                      Diagnosis Codes (Optional)
                    </h5>
                    <div className='space-y-2'>
                      {(
                        formData.subscriber.healthCareCodeInformation || []
                      ).map((diagnosis, index) => (
                        <div key={index} className='flex gap-2'>
                          <Select
                            value={diagnosis.diagnosisTypeCode || ''}
                            onValueChange={value => {
                              const newDiagnoses = [
                                ...(formData.subscriber
                                  .healthCareCodeInformation || []),
                              ];
                              newDiagnoses[index] = {
                                ...newDiagnoses[index],
                                diagnosisTypeCode: value,
                              };
                              setFormData(prev => ({
                                ...prev,
                                subscriber: {
                                  ...prev.subscriber,
                                  healthCareCodeInformation: newDiagnoses,
                                },
                              }));
                            }}
                          >
                            <SelectTrigger className='w-48'>
                              <SelectValue placeholder='Type' />
                            </SelectTrigger>
                            <SelectContent>
                              {diagnosisTypeCodeOptions.map(option => (
                                <SelectItem
                                  key={option.value}
                                  value={option.value}
                                >
                                  {option.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>

                          <Input
                            value={diagnosis.diagnosisCode || ''}
                            onChange={e => {
                              const newDiagnoses = [
                                ...(formData.subscriber
                                  .healthCareCodeInformation || []),
                              ];
                              newDiagnoses[index] = {
                                ...newDiagnoses[index],
                                diagnosisCode: e.target.value,
                              };
                              setFormData(prev => ({
                                ...prev,
                                subscriber: {
                                  ...prev.subscriber,
                                  healthCareCodeInformation: newDiagnoses,
                                },
                              }));
                            }}
                            placeholder='Diagnosis code (e.g., M79.3)'
                            className='flex-1'
                          />

                          <Button
                            type='button'
                            variant='destructive'
                            size='sm'
                            onClick={() => {
                              setFormData(prev => ({
                                ...prev,
                                subscriber: {
                                  ...prev.subscriber,
                                  healthCareCodeInformation:
                                    prev.subscriber.healthCareCodeInformation?.filter(
                                      (_, i) => i !== index
                                    ),
                                },
                              }));
                            }}
                          >
                            Remove
                          </Button>
                        </div>
                      ))}

                      <Button
                        type='button'
                        variant='outline'
                        size='sm'
                        onClick={() => {
                          setFormData(prev => {
                            const existingCodes =
                              prev.subscriber.healthCareCodeInformation || [];
                            const isFirst = existingCodes.length === 0;
                            return {
                              ...prev,
                              subscriber: {
                                ...prev.subscriber,
                                healthCareCodeInformation: [
                                  ...existingCodes,
                                  {
                                    diagnosisTypeCode: isFirst ? 'ABK' : 'ABF',
                                    diagnosisCode: '',
                                  },
                                ],
                              },
                            };
                          });
                        }}
                      >
                        Add Diagnosis Code
                      </Button>
                    </div>
                  </div>

                  {/* Additional Identification */}
                  <div className='mt-4'>
                    <h5 className='text-sm font-medium mb-2'>
                      Additional Identification (Optional)
                    </h5>
                    <div className='grid grid-cols-2 gap-4'>
                      <div>
                        <label
                          htmlFor='planNumber'
                          className='block text-sm font-medium mb-2'
                        >
                          Plan Number
                        </label>
                        <Input
                          id='planNumber'
                          value={
                            formData.subscriber.additionalIdentification
                              ?.planNumber || ''
                          }
                          onChange={e =>
                            setFormData(prev => ({
                              ...prev,
                              subscriber: {
                                ...prev.subscriber,
                                additionalIdentification: {
                                  ...prev.subscriber.additionalIdentification,
                                  planNumber: e.target.value,
                                },
                              },
                            }))
                          }
                          placeholder='Plan number'
                        />
                      </div>
                      <div>
                        <label
                          htmlFor='policyNumber'
                          className='block text-sm font-medium mb-2'
                        >
                          Policy Number
                        </label>
                        <Input
                          id='policyNumber'
                          value={
                            formData.subscriber.additionalIdentification
                              ?.policyNumber || ''
                          }
                          onChange={e =>
                            setFormData(prev => ({
                              ...prev,
                              subscriber: {
                                ...prev.subscriber,
                                additionalIdentification: {
                                  ...prev.subscriber.additionalIdentification,
                                  policyNumber: e.target.value,
                                },
                              },
                            }))
                          }
                          placeholder='Policy number'
                        />
                      </div>
                      <div>
                        <label
                          htmlFor='memberIdentificationNumber'
                          className='block text-sm font-medium mb-2'
                        >
                          Member Identification Number
                        </label>
                        <Input
                          id='memberIdentificationNumber'
                          value={
                            formData.subscriber.additionalIdentification
                              ?.memberIdentificationNumber || ''
                          }
                          onChange={e =>
                            setFormData(prev => ({
                              ...prev,
                              subscriber: {
                                ...prev.subscriber,
                                additionalIdentification: {
                                  ...prev.subscriber.additionalIdentification,
                                  memberIdentificationNumber: e.target.value,
                                },
                              },
                            }))
                          }
                          placeholder='Member ID'
                        />
                      </div>
                      <div>
                        <label
                          htmlFor='contractNumber'
                          className='block text-sm font-medium mb-2'
                        >
                          Contract Number
                        </label>
                        <Input
                          id='contractNumber'
                          value={
                            formData.subscriber.additionalIdentification
                              ?.contractNumber || ''
                          }
                          onChange={e =>
                            setFormData(prev => ({
                              ...prev,
                              subscriber: {
                                ...prev.subscriber,
                                additionalIdentification: {
                                  ...prev.subscriber.additionalIdentification,
                                  contractNumber: e.target.value,
                                },
                              },
                            }))
                          }
                          placeholder='Contract number'
                        />
                      </div>
                      <div>
                        <label
                          htmlFor='medicalRecordIdentificationNumber'
                          className='block text-sm font-medium mb-2'
                        >
                          Medical Record ID
                        </label>
                        <Input
                          id='medicalRecordIdentificationNumber'
                          value={
                            formData.subscriber.additionalIdentification
                              ?.medicalRecordIdentificationNumber || ''
                          }
                          onChange={e =>
                            setFormData(prev => ({
                              ...prev,
                              subscriber: {
                                ...prev.subscriber,
                                additionalIdentification: {
                                  ...prev.subscriber.additionalIdentification,
                                  medicalRecordIdentificationNumber:
                                    e.target.value,
                                },
                              },
                            }))
                          }
                          placeholder='Medical record ID'
                        />
                      </div>
                      <div>
                        <label
                          htmlFor='patientAccountNumber'
                          className='block text-sm font-medium mb-2'
                        >
                          Patient Account Number
                        </label>
                        <Input
                          id='patientAccountNumber'
                          value={
                            formData.subscriber.additionalIdentification
                              ?.patientAccountNumber || ''
                          }
                          onChange={e =>
                            setFormData(prev => ({
                              ...prev,
                              subscriber: {
                                ...prev.subscriber,
                                additionalIdentification: {
                                  ...prev.subscriber.additionalIdentification,
                                  patientAccountNumber: e.target.value,
                                },
                              },
                            }))
                          }
                          placeholder='Patient account #'
                        />
                      </div>
                      <div>
                        <label
                          htmlFor='healthInsuranceClaimNumber'
                          className='block text-sm font-medium mb-2'
                        >
                          Health Insurance Claim Number
                        </label>
                        <Input
                          id='healthInsuranceClaimNumber'
                          value={
                            formData.subscriber.additionalIdentification
                              ?.healthInsuranceClaimNumber || ''
                          }
                          onChange={e =>
                            setFormData(prev => ({
                              ...prev,
                              subscriber: {
                                ...prev.subscriber,
                                additionalIdentification: {
                                  ...prev.subscriber.additionalIdentification,
                                  healthInsuranceClaimNumber: e.target.value,
                                },
                              },
                            }))
                          }
                          placeholder='HIC number'
                        />
                      </div>
                      <div>
                        <label
                          htmlFor='identificationCardSerialNumber'
                          className='block text-sm font-medium mb-2'
                        >
                          ID Card Serial Number
                        </label>
                        <Input
                          id='identificationCardSerialNumber'
                          value={
                            formData.subscriber.additionalIdentification
                              ?.identificationCardSerialNumber || ''
                          }
                          onChange={e =>
                            setFormData(prev => ({
                              ...prev,
                              subscriber: {
                                ...prev.subscriber,
                                additionalIdentification: {
                                  ...prev.subscriber.additionalIdentification,
                                  identificationCardSerialNumber:
                                    e.target.value,
                                },
                              },
                            }))
                          }
                          placeholder='ID card serial #'
                        />
                      </div>
                      <div>
                        <label
                          htmlFor='insurancePolicyNumber'
                          className='block text-sm font-medium mb-2'
                        >
                          Insurance Policy Number
                        </label>
                        <Input
                          id='insurancePolicyNumber'
                          value={
                            formData.subscriber.additionalIdentification
                              ?.insurancePolicyNumber || ''
                          }
                          onChange={e =>
                            setFormData(prev => ({
                              ...prev,
                              subscriber: {
                                ...prev.subscriber,
                                additionalIdentification: {
                                  ...prev.subscriber.additionalIdentification,
                                  insurancePolicyNumber: e.target.value,
                                },
                              },
                            }))
                          }
                          placeholder='Insurance policy #'
                        />
                      </div>
                      <div>
                        <label
                          htmlFor='planNetworkIdentificationNumber'
                          className='block text-sm font-medium mb-2'
                        >
                          Plan Network ID Number
                        </label>
                        <Input
                          id='planNetworkIdentificationNumber'
                          value={
                            formData.subscriber.additionalIdentification
                              ?.planNetworkIdentificationNumber || ''
                          }
                          onChange={e =>
                            setFormData(prev => ({
                              ...prev,
                              subscriber: {
                                ...prev.subscriber,
                                additionalIdentification: {
                                  ...prev.subscriber.additionalIdentification,
                                  planNetworkIdentificationNumber:
                                    e.target.value,
                                },
                              },
                            }))
                          }
                          placeholder='Network ID'
                        />
                      </div>
                      <div>
                        <label
                          htmlFor='agencyClaimNumber'
                          className='block text-sm font-medium mb-2'
                        >
                          Agency Claim Number
                        </label>
                        <Input
                          id='agencyClaimNumber'
                          value={
                            formData.subscriber.additionalIdentification
                              ?.agencyClaimNumber || ''
                          }
                          onChange={e =>
                            setFormData(prev => ({
                              ...prev,
                              subscriber: {
                                ...prev.subscriber,
                                additionalIdentification: {
                                  ...prev.subscriber.additionalIdentification,
                                  agencyClaimNumber: e.target.value,
                                },
                              },
                            }))
                          }
                          placeholder='Agency claim #'
                        />
                      </div>
                    </div>
                  </div>
                </details>
              </div>

              {/* Dependents Section */}
              <div className='space-y-4'>
                <h3 className='text-lg font-semibold'>Dependents (Optional)</h3>
                <div className='space-y-4'>
                  {(formData.dependents || []).map((dependent, index) => (
                    <div
                      key={index}
                      className='border rounded-lg p-4 space-y-4'
                    >
                      <div className='flex justify-between items-center mb-4'>
                        <h4 className='font-medium'>Dependent {index + 1}</h4>
                        <Button
                          type='button'
                          variant='destructive'
                          size='sm'
                          onClick={() => {
                            setFormData(prev => ({
                              ...prev,
                              dependents: prev.dependents?.filter(
                                (_, i) => i !== index
                              ),
                            }));
                          }}
                        >
                          Remove
                        </Button>
                      </div>

                      <div className='grid grid-cols-2 gap-4'>
                        <div>
                          <label className='block text-sm font-medium mb-2'>
                            Relationship
                          </label>
                          <Select
                            value={dependent.individualRelationshipCode || ''}
                            onValueChange={value => {
                              const newDependents = [
                                ...(formData.dependents || []),
                              ];
                              newDependents[index] = {
                                ...newDependents[index],
                                individualRelationshipCode: value,
                              };
                              setFormData(prev => ({
                                ...prev,
                                dependents: newDependents,
                              }));
                            }}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder='Select relationship' />
                            </SelectTrigger>
                            <SelectContent>
                              {individualRelationshipCodeOptions.map(option => (
                                <SelectItem
                                  key={option.value}
                                  value={option.value}
                                >
                                  {option.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>

                        <div>
                          <label className='block text-sm font-medium mb-2'>
                            Member ID
                          </label>
                          <Input
                            value={dependent.memberId || ''}
                            onChange={e => {
                              const newDependents = [
                                ...(formData.dependents || []),
                              ];
                              newDependents[index] = {
                                ...newDependents[index],
                                memberId: e.target.value,
                              };
                              setFormData(prev => ({
                                ...prev,
                                dependents: newDependents,
                              }));
                            }}
                            placeholder='Member ID'
                          />
                        </div>

                        <div>
                          <label className='block text-sm font-medium mb-2'>
                            First Name
                          </label>
                          <Input
                            value={dependent.firstName || ''}
                            onChange={e => {
                              const newDependents = [
                                ...(formData.dependents || []),
                              ];
                              newDependents[index] = {
                                ...newDependents[index],
                                firstName: e.target.value,
                              };
                              setFormData(prev => ({
                                ...prev,
                                dependents: newDependents,
                              }));
                            }}
                            placeholder='First name'
                          />
                        </div>

                        <div>
                          <label className='block text-sm font-medium mb-2'>
                            Last Name
                          </label>
                          <Input
                            value={dependent.lastName || ''}
                            onChange={e => {
                              const newDependents = [
                                ...(formData.dependents || []),
                              ];
                              newDependents[index] = {
                                ...newDependents[index],
                                lastName: e.target.value,
                              };
                              setFormData(prev => ({
                                ...prev,
                                dependents: newDependents,
                              }));
                            }}
                            placeholder='Last name'
                          />
                        </div>

                        <div>
                          <label className='block text-sm font-medium mb-2'>
                            Date of Birth
                          </label>
                          <Input
                            type='date'
                            value={
                              dependent.dateOfBirth
                                ? `${dependent.dateOfBirth.slice(0, 4)}-${dependent.dateOfBirth.slice(4, 6)}-${dependent.dateOfBirth.slice(6, 8)}`
                                : ''
                            }
                            onChange={e => {
                              const newDependents = [
                                ...(formData.dependents || []),
                              ];
                              newDependents[index] = {
                                ...newDependents[index],
                                dateOfBirth: e.target.value.replace(/-/g, ''),
                              };
                              setFormData(prev => ({
                                ...prev,
                                dependents: newDependents,
                              }));
                            }}
                          />
                        </div>

                        <div>
                          <label className='block text-sm font-medium mb-2'>
                            Gender
                          </label>
                          <Select
                            value={dependent.gender || ''}
                            onValueChange={value => {
                              const newDependents = [
                                ...(formData.dependents || []),
                              ];
                              newDependents[index] = {
                                ...newDependents[index],
                                gender: value as 'M' | 'F',
                              };
                              setFormData(prev => ({
                                ...prev,
                                dependents: newDependents,
                              }));
                            }}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value='M'>Male</SelectItem>
                              <SelectItem value='F'>Female</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>

                        <div>
                          <label className='block text-sm font-medium mb-2'>
                            Middle Name
                          </label>
                          <Input
                            value={dependent.middleName || ''}
                            onChange={e => {
                              const newDependents = [
                                ...(formData.dependents || []),
                              ];
                              newDependents[index] = {
                                ...newDependents[index],
                                middleName: e.target.value,
                              };
                              setFormData(prev => ({
                                ...prev,
                                dependents: newDependents,
                              }));
                            }}
                            placeholder='Middle name'
                          />
                        </div>

                        <div>
                          <label className='block text-sm font-medium mb-2'>
                            Suffix
                          </label>
                          <Input
                            value={dependent.suffix || ''}
                            onChange={e => {
                              const newDependents = [
                                ...(formData.dependents || []),
                              ];
                              newDependents[index] = {
                                ...newDependents[index],
                                suffix: e.target.value,
                              };
                              setFormData(prev => ({
                                ...prev,
                                dependents: newDependents,
                              }));
                            }}
                            placeholder='Jr., Sr., etc.'
                          />
                        </div>

                        <div>
                          <label className='block text-sm font-medium mb-2'>
                            SSN
                          </label>
                          <Input
                            value={dependent.ssn || ''}
                            onChange={e => {
                              const newDependents = [
                                ...(formData.dependents || []),
                              ];
                              newDependents[index] = {
                                ...newDependents[index],
                                ssn: e.target.value,
                              };
                              setFormData(prev => ({
                                ...prev,
                                dependents: newDependents,
                              }));
                            }}
                            placeholder='123456789'
                            maxLength={9}
                          />
                        </div>

                        <div>
                          <label className='block text-sm font-medium mb-2'>
                            Group Number
                          </label>
                          <Input
                            value={dependent.groupNumber || ''}
                            onChange={e => {
                              const newDependents = [
                                ...(formData.dependents || []),
                              ];
                              newDependents[index] = {
                                ...newDependents[index],
                                groupNumber: e.target.value,
                              };
                              setFormData(prev => ({
                                ...prev,
                                dependents: newDependents,
                              }));
                            }}
                            placeholder='Group #'
                          />
                        </div>

                        <div>
                          <label className='block text-sm font-medium mb-2'>
                            ID Card
                          </label>
                          <Input
                            value={dependent.idCard || ''}
                            onChange={e => {
                              const newDependents = [
                                ...(formData.dependents || []),
                              ];
                              newDependents[index] = {
                                ...newDependents[index],
                                idCard: e.target.value,
                              };
                              setFormData(prev => ({
                                ...prev,
                                dependents: newDependents,
                              }));
                            }}
                            placeholder='ID card #'
                          />
                        </div>
                      </div>

                      {/* Dependent Address */}
                      <div className='mt-4'>
                        <h5 className='text-sm font-medium mb-2'>Address</h5>
                        <div className='grid grid-cols-2 gap-4'>
                          <div>
                            <label className='block text-sm font-medium mb-2'>
                              Address Line 1
                            </label>
                            <Input
                              value={dependent.address?.address1 || ''}
                              onChange={e => {
                                const newDependents = [
                                  ...(formData.dependents || []),
                                ];
                                newDependents[index] = {
                                  ...newDependents[index],
                                  address: {
                                    ...newDependents[index].address,
                                    address1: e.target.value,
                                    city:
                                      newDependents[index].address?.city || '',
                                  } as StediAddress,
                                };
                                setFormData(prev => ({
                                  ...prev,
                                  dependents: newDependents,
                                }));
                              }}
                              placeholder='123 Main St'
                            />
                          </div>
                          <div>
                            <label className='block text-sm font-medium mb-2'>
                              Address Line 2
                            </label>
                            <Input
                              value={dependent.address?.address2 || ''}
                              onChange={e => {
                                const newDependents = [
                                  ...(formData.dependents || []),
                                ];
                                newDependents[index] = {
                                  ...newDependents[index],
                                  address: {
                                    ...newDependents[index].address,
                                    address1:
                                      newDependents[index].address?.address1 ||
                                      '',
                                    address2: e.target.value,
                                    city:
                                      newDependents[index].address?.city || '',
                                  } as StediAddress,
                                };
                                setFormData(prev => ({
                                  ...prev,
                                  dependents: newDependents,
                                }));
                              }}
                              placeholder='Apt 2B'
                            />
                          </div>
                          <div>
                            <label className='block text-sm font-medium mb-2'>
                              City
                            </label>
                            <Input
                              value={dependent.address?.city || ''}
                              onChange={e => {
                                const newDependents = [
                                  ...(formData.dependents || []),
                                ];
                                newDependents[index] = {
                                  ...newDependents[index],
                                  address: {
                                    ...newDependents[index].address,
                                    address1:
                                      newDependents[index].address?.address1 ||
                                      '',
                                    city: e.target.value,
                                  } as StediAddress,
                                };
                                setFormData(prev => ({
                                  ...prev,
                                  dependents: newDependents,
                                }));
                              }}
                              placeholder='Houston'
                            />
                          </div>
                          <div>
                            <label className='block text-sm font-medium mb-2'>
                              State
                            </label>
                            <Input
                              value={dependent.address?.state || ''}
                              onChange={e => {
                                const newDependents = [
                                  ...(formData.dependents || []),
                                ];
                                newDependents[index] = {
                                  ...newDependents[index],
                                  address: {
                                    ...newDependents[index].address,
                                    address1:
                                      newDependents[index].address?.address1 ||
                                      '',
                                    city:
                                      newDependents[index].address?.city || '',
                                    state: e.target.value,
                                  } as StediAddress,
                                };
                                setFormData(prev => ({
                                  ...prev,
                                  dependents: newDependents,
                                }));
                              }}
                              placeholder='TX'
                              maxLength={2}
                            />
                          </div>
                          <div>
                            <label className='block text-sm font-medium mb-2'>
                              Postal Code
                            </label>
                            <Input
                              value={dependent.address?.postalCode || ''}
                              onChange={e => {
                                const newDependents = [
                                  ...(formData.dependents || []),
                                ];
                                newDependents[index] = {
                                  ...newDependents[index],
                                  address: {
                                    ...newDependents[index].address,
                                    address1:
                                      newDependents[index].address?.address1 ||
                                      '',
                                    city:
                                      newDependents[index].address?.city || '',
                                    postalCode: e.target.value,
                                  } as StediAddress,
                                };
                                setFormData(prev => ({
                                  ...prev,
                                  dependents: newDependents,
                                }));
                              }}
                              placeholder='77001'
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}

                  <Button
                    type='button'
                    variant='outline'
                    onClick={() => {
                      setFormData(prev => ({
                        ...prev,
                        dependents: [
                          ...(prev.dependents || []),
                          {
                            individualRelationshipCode: '19',
                            firstName: '',
                            lastName: '',
                            dateOfBirth: '',
                            gender: 'M' as const,
                            middleName: '',
                            suffix: '',
                            ssn: '',
                            memberId: '',
                            groupNumber: '',
                            idCard: '',
                          },
                        ],
                      }));
                    }}
                  >
                    Add Dependent
                  </Button>
                </div>
              </div>

              {/* Request Metadata */}
              <div className='space-y-4'>
                <h3 className='text-lg font-semibold'>Request Metadata</h3>
                <div className='grid grid-cols-2 gap-4'>
                  <div>
                    <label
                      htmlFor='externalPatientId'
                      className='block text-sm font-medium mb-2'
                    >
                      External Patient ID (Optional)
                    </label>
                    <Input
                      id='externalPatientId'
                      value={formData.externalPatientId || ''}
                      onChange={e =>
                        setFormData(prev => ({
                          ...prev,
                          externalPatientId: e.target.value,
                        }))
                      }
                      placeholder='Your patient ID'
                    />
                  </div>
                  <div>
                    <label
                      htmlFor='submitterTransactionIdentifier'
                      className='block text-sm font-medium mb-2'
                    >
                      Submitter Transaction ID (Optional)
                    </label>
                    <Input
                      id='submitterTransactionIdentifier'
                      value={formData.submitterTransactionIdentifier || ''}
                      onChange={e =>
                        setFormData(prev => ({
                          ...prev,
                          submitterTransactionIdentifier: e.target.value,
                        }))
                      }
                      placeholder='For batch requests'
                    />
                  </div>
                </div>

                {/* Portal Credentials (collapsed by default) */}
                <details className='mt-4'>
                  <summary className='cursor-pointer text-sm font-medium text-muted-foreground'>
                    Portal Credentials (Optional)
                  </summary>
                  <div className='grid grid-cols-2 gap-4 mt-4'>
                    <div>
                      <label
                        htmlFor='portalUsername'
                        className='block text-sm font-medium mb-2'
                      >
                        Portal Username
                      </label>
                      <Input
                        id='portalUsername'
                        value={formData.portalUsername || ''}
                        onChange={e =>
                          setFormData(prev => ({
                            ...prev,
                            portalUsername: e.target.value,
                          }))
                        }
                        placeholder='Provider portal username'
                      />
                    </div>
                    <div>
                      <label
                        htmlFor='portalPassword'
                        className='block text-sm font-medium mb-2'
                      >
                        Portal Password
                      </label>
                      <Input
                        id='portalPassword'
                        type='password'
                        value={formData.portalPassword || ''}
                        onChange={e =>
                          setFormData(prev => ({
                            ...prev,
                            portalPassword: e.target.value,
                          }))
                        }
                        placeholder='Provider portal password'
                      />
                    </div>
                  </div>
                </details>
              </div>

              {/* Payor Selection */}
              <div className='space-y-4'>
                <h3 className='text-lg font-semibold'>Payor Information</h3>
                <div>
                  <label
                    htmlFor='payor'
                    className='block text-sm font-medium mb-2'
                  >
                    Trading Partner (Payor)
                  </label>
                  <Select
                    value={formData.tradingPartnerServiceId}
                    onValueChange={value =>
                      setFormData(prev => ({
                        ...prev,
                        tradingPartnerServiceId: value,
                      }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder='Select a payor' />
                    </SelectTrigger>
                    <SelectContent>
                      {stediPayerOptions.map(option => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Service Information */}
              <div className='space-y-4'>
                <h3 className='text-lg font-semibold'>Service Information</h3>
                <div>
                  <label
                    htmlFor='controlNumber'
                    className='block text-sm font-medium mb-2'
                  >
                    Control Number (9 digits)
                  </label>
                  <Input
                    id='controlNumber'
                    value={formData.controlNumber}
                    onChange={e =>
                      setFormData(prev => ({
                        ...prev,
                        controlNumber: e.target.value,
                      }))
                    }
                    placeholder='123456789'
                    maxLength={9}
                  />
                </div>
                <div>
                  <label className='block text-sm font-medium mb-2'>
                    Service Type Codes
                  </label>
                  <div className='grid grid-cols-2 gap-2 mt-2'>
                    {serviceTypeOptions.map(option => (
                      <label
                        key={option.value}
                        className='flex items-center space-x-2'
                      >
                        <input
                          type='checkbox'
                          checked={
                            formData.encounter?.serviceTypeCodes?.includes(
                              option.value
                            ) || false
                          }
                          onChange={e => {
                            const currentTypes =
                              formData.encounter?.serviceTypeCodes || [];
                            if (e.target.checked) {
                              handleServiceTypesChange([
                                ...currentTypes,
                                option.value,
                              ]);
                            } else {
                              handleServiceTypesChange(
                                currentTypes.filter(t => t !== option.value)
                              );
                            }
                          }}
                          className='rounded'
                        />
                        <span className='text-sm'>{option.label}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Date of Service Options */}
                <div className='space-y-4'>
                  <p className='text-sm text-muted-foreground'>
                    Choose either a single date of service OR a date range (not
                    both)
                  </p>
                  <div className='grid grid-cols-2 gap-4'>
                    <div>
                      <label
                        htmlFor='dateOfService'
                        className='block text-sm font-medium mb-2'
                      >
                        Date of Service
                      </label>
                      <Input
                        id='dateOfService'
                        type='date'
                        value={
                          formData.encounter?.dateOfService
                            ? `${formData.encounter.dateOfService.slice(0, 4)}-${formData.encounter.dateOfService.slice(4, 6)}-${formData.encounter.dateOfService.slice(6, 8)}`
                            : ''
                        }
                        onChange={e => {
                          const value = e.target.value.replace(/-/g, '');
                          setFormData(prev => ({
                            ...prev,
                            encounter: {
                              ...prev.encounter,
                              dateOfService: value,
                              // Clear date range if single date is set
                              beginningDateOfService: undefined,
                              endDateOfService: undefined,
                            },
                          }));
                        }}
                      />
                    </div>
                  </div>

                  <div className='text-center text-sm text-muted-foreground'>
                    OR
                  </div>

                  <div className='grid grid-cols-2 gap-4'>
                    <div>
                      <label
                        htmlFor='beginningDateOfService'
                        className='block text-sm font-medium mb-2'
                      >
                        Beginning Date of Service
                      </label>
                      <Input
                        id='beginningDateOfService'
                        type='date'
                        value={
                          formData.encounter?.beginningDateOfService
                            ? `${formData.encounter.beginningDateOfService.slice(0, 4)}-${formData.encounter.beginningDateOfService.slice(4, 6)}-${formData.encounter.beginningDateOfService.slice(6, 8)}`
                            : ''
                        }
                        onChange={e => {
                          const value = e.target.value.replace(/-/g, '');
                          setFormData(prev => ({
                            ...prev,
                            encounter: {
                              ...prev.encounter,
                              beginningDateOfService: value,
                              // Clear single date if range is set
                              dateOfService: undefined,
                            },
                          }));
                        }}
                      />
                    </div>
                    <div>
                      <label
                        htmlFor='endDateOfService'
                        className='block text-sm font-medium mb-2'
                      >
                        End Date of Service
                      </label>
                      <Input
                        id='endDateOfService'
                        type='date'
                        value={
                          formData.encounter?.endDateOfService
                            ? `${formData.encounter.endDateOfService.slice(0, 4)}-${formData.encounter.endDateOfService.slice(4, 6)}-${formData.encounter.endDateOfService.slice(6, 8)}`
                            : ''
                        }
                        onChange={e => {
                          const value = e.target.value.replace(/-/g, '');
                          setFormData(prev => ({
                            ...prev,
                            encounter: {
                              ...prev.encounter,
                              endDateOfService: value,
                              // Clear single date if range is set
                              dateOfService: undefined,
                            },
                          }));
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* Procedure Code (Optional) */}
                <div className='grid grid-cols-2 gap-4'>
                  <div>
                    <label
                      htmlFor='procedureCode'
                      className='block text-sm font-medium mb-2'
                    >
                      Procedure Code (Optional)
                    </label>
                    <Input
                      id='procedureCode'
                      value={formData.encounter?.procedureCode || ''}
                      onChange={e =>
                        setFormData(prev => ({
                          ...prev,
                          encounter: {
                            ...prev.encounter,
                            procedureCode: e.target.value,
                          },
                        }))
                      }
                      placeholder='e.g., 99213'
                    />
                  </div>
                  <div>
                    <label
                      htmlFor='productOrServiceIDQualifier'
                      className='block text-sm font-medium mb-2'
                    >
                      Code Type (Required if procedure code is set)
                    </label>
                    <Select
                      value={
                        formData.encounter?.productOrServiceIDQualifier || ''
                      }
                      onValueChange={value =>
                        setFormData(prev => ({
                          ...prev,
                          encounter: {
                            ...prev.encounter,
                            productOrServiceIDQualifier: value,
                          },
                        }))
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder='Select code type' />
                      </SelectTrigger>
                      <SelectContent>
                        {productOrServiceIDQualifierOptions.map(option => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Prior Authorization/Referral */}
                <div className='grid grid-cols-2 gap-4'>
                  <div>
                    <label
                      htmlFor='priorAuthorizationOrReferralNumber'
                      className='block text-sm font-medium mb-2'
                    >
                      Prior Auth/Referral Number (Optional)
                    </label>
                    <Input
                      id='priorAuthorizationOrReferralNumber'
                      value={
                        formData.encounter
                          ?.priorAuthorizationOrReferralNumber || ''
                      }
                      onChange={e =>
                        setFormData(prev => ({
                          ...prev,
                          encounter: {
                            ...prev.encounter,
                            priorAuthorizationOrReferralNumber: e.target.value,
                          },
                        }))
                      }
                      placeholder='PA123456'
                    />
                  </div>
                  <div>
                    <label
                      htmlFor='referenceIdentificationQualifier'
                      className='block text-sm font-medium mb-2'
                    >
                      Reference Type (If auth/referral number provided)
                    </label>
                    <Select
                      value={
                        formData.encounter?.referenceIdentificationQualifier ||
                        ''
                      }
                      onValueChange={value =>
                        setFormData(prev => ({
                          ...prev,
                          encounter: {
                            ...prev.encounter,
                            referenceIdentificationQualifier: value,
                          },
                        }))
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder='Select reference type' />
                      </SelectTrigger>
                      <SelectContent>
                        {referenceIdentificationQualifierOptions.map(option => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Place of Service */}
                <div>
                  <label
                    htmlFor='industryCode'
                    className='block text-sm font-medium mb-2'
                  >
                    Place of Service Code (Optional)
                  </label>
                  <Input
                    id='industryCode'
                    value={formData.encounter?.industryCode || ''}
                    onChange={e =>
                      setFormData(prev => ({
                        ...prev,
                        encounter: {
                          ...prev.encounter,
                          industryCode: e.target.value,
                        },
                      }))
                    }
                    placeholder='e.g., 11 (Office)'
                  />
                </div>

                {/* Medical Procedures Array */}
                <div className='mt-6'>
                  <h4 className='text-md font-semibold mb-4'>
                    Multiple Procedure Codes (Optional)
                  </h4>
                  <div className='space-y-4'>
                    {(formData.encounter?.medicalProcedures || []).map(
                      (procedure, index) => (
                        <div
                          key={index}
                          className='border rounded-lg p-4 space-y-4'
                        >
                          <div className='flex justify-between items-center mb-2'>
                            <h5 className='font-medium'>
                              Procedure {index + 1}
                            </h5>
                            <Button
                              type='button'
                              variant='destructive'
                              size='sm'
                              onClick={() => {
                                setFormData(prev => ({
                                  ...prev,
                                  encounter: {
                                    ...prev.encounter,
                                    medicalProcedures:
                                      prev.encounter?.medicalProcedures?.filter(
                                        (_, i) => i !== index
                                      ),
                                  },
                                }));
                              }}
                            >
                              Remove
                            </Button>
                          </div>

                          <div className='grid grid-cols-2 gap-4'>
                            <div>
                              <label className='block text-sm font-medium mb-2'>
                                Procedure Code
                              </label>
                              <Input
                                value={procedure.procedureCode || ''}
                                onChange={e => {
                                  const newProcedures = [
                                    ...(formData.encounter?.medicalProcedures ||
                                      []),
                                  ];
                                  newProcedures[index] = {
                                    ...newProcedures[index],
                                    procedureCode: e.target.value,
                                  };
                                  setFormData(prev => ({
                                    ...prev,
                                    encounter: {
                                      ...prev.encounter,
                                      medicalProcedures: newProcedures,
                                    },
                                  }));
                                }}
                                placeholder='e.g., 99213'
                              />
                            </div>

                            <div>
                              <label className='block text-sm font-medium mb-2'>
                                Code Type
                              </label>
                              <Select
                                value={
                                  procedure.qualifier ||
                                  procedure.productOrServiceIDQualifier ||
                                  ''
                                }
                                onValueChange={value => {
                                  const newProcedures = [
                                    ...(formData.encounter?.medicalProcedures ||
                                      []),
                                  ];
                                  newProcedures[index] = {
                                    ...newProcedures[index],
                                    qualifier: value,
                                    productOrServiceIDQualifier: value,
                                  };
                                  setFormData(prev => ({
                                    ...prev,
                                    encounter: {
                                      ...prev.encounter,
                                      medicalProcedures: newProcedures,
                                    },
                                  }));
                                }}
                              >
                                <SelectTrigger>
                                  <SelectValue placeholder='Select code type' />
                                </SelectTrigger>
                                <SelectContent>
                                  {productOrServiceIDQualifierOptions.map(
                                    option => (
                                      <SelectItem
                                        key={option.value}
                                        value={option.value}
                                      >
                                        {option.label}
                                      </SelectItem>
                                    )
                                  )}
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                        </div>
                      )
                    )}

                    <Button
                      type='button'
                      variant='outline'
                      size='sm'
                      onClick={() => {
                        setFormData(prev => ({
                          ...prev,
                          encounter: {
                            ...prev.encounter,
                            medicalProcedures: [
                              ...(prev.encounter?.medicalProcedures || []),
                              {
                                qualifier: 'CJ',
                                procedureCode: '',
                                productOrServiceIDQualifier: 'CJ',
                              },
                            ],
                          },
                        }));
                      }}
                    >
                      Add Procedure Code
                    </Button>
                  </div>
                </div>
              </div>

              <Button
                onClick={handleSubmit}
                disabled={isLoading}
                className='w-full'
              >
                {isLoading
                  ? 'Checking Eligibility...'
                  : 'Submit Eligibility Check'}
              </Button>

              {/* Request Preview */}
              <div className='mt-6'>
                <label className='block text-sm font-medium mb-2'>
                  Request Preview (JSON)
                </label>
                <pre className='bg-muted p-4 rounded-md text-xs overflow-auto font-mono max-h-96'>
                  {JSON.stringify(formData, null, 2)}
                </pre>
              </div>
            </TabsContent>

            <TabsContent value='response' className='space-y-6'>
              {response ? (
                <>
                  {/* Status Summary */}
                  {response.errors && response.errors.length > 0 ? (
                    <div className='p-4 bg-red-50 border border-red-200 rounded-lg'>
                      <h3 className='text-lg font-semibold text-red-800 mb-2'>
                        Errors
                      </h3>
                      {response.errors.map((err, idx) => (
                        <div key={idx} className='mb-2'>
                          <p className='text-red-700 font-medium'>
                            {err.code}: {err.description}
                          </p>
                          {err.followupActionCode && (
                            <p className='text-red-600 text-sm'>
                              {err.followupActionCode}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className='p-4 bg-green-50 border border-green-200 rounded-lg'>
                      <h3 className='text-lg font-semibold text-green-800'>
                        Eligibility Check Successful
                      </h3>
                    </div>
                  )}

                  {/* Subscriber Information */}
                  {response.subscriber && (
                    <div>
                      <h3 className='text-lg font-semibold mb-3'>
                        Subscriber Information
                      </h3>
                      <div className='bg-muted p-4 rounded-md space-y-2'>
                        <p>
                          <span className='font-medium'>Name:</span>{' '}
                          {response.subscriber.firstName}{' '}
                          {response.subscriber.lastName}
                        </p>
                        <p>
                          <span className='font-medium'>Member ID:</span>{' '}
                          {response.subscriber.memberId}
                        </p>
                        {response.subscriber.groupNumber && (
                          <p>
                            <span className='font-medium'>Group Number:</span>{' '}
                            {response.subscriber.groupNumber}
                          </p>
                        )}
                        {response.subscriber.planDescription && (
                          <p>
                            <span className='font-medium'>Plan:</span>{' '}
                            {response.subscriber.planDescription}
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Benefits Information */}
                  {response.benefitsInformation &&
                    response.benefitsInformation.length > 0 && (
                      <div>
                        <h3 className='text-lg font-semibold mb-3'>
                          Benefits Information
                        </h3>
                        <div className='space-y-3'>
                          {response.benefitsInformation.map((benefit, idx) => (
                            <div
                              key={idx}
                              className='bg-muted p-4 rounded-md space-y-2'
                            >
                              <p className='font-medium'>
                                {benefit.name || benefit.code}
                              </p>
                              {benefit.coverageLevelCode && (
                                <p className='text-sm'>
                                  Coverage Level: {benefit.coverageLevel}
                                </p>
                              )}
                              {benefit.serviceTypes &&
                                benefit.serviceTypes.length > 0 && (
                                  <p className='text-sm'>
                                    Service Types:{' '}
                                    {benefit.serviceTypes.join(', ')}
                                    {benefit.serviceTypeCodes &&
                                      benefit.serviceTypeCodes.length > 0 && (
                                        <span className='text-xs text-muted-foreground ml-1'>
                                          ({benefit.serviceTypeCodes.join(', ')}
                                          )
                                        </span>
                                      )}
                                  </p>
                                )}
                              {benefit.insuranceType && (
                                <p className='text-sm'>
                                  Insurance Type: {benefit.insuranceType}
                                </p>
                              )}
                              {benefit.planCoverage && (
                                <p className='text-sm'>
                                  Plan Coverage: {benefit.planCoverage}
                                </p>
                              )}
                              {benefit.benefitAmount && (
                                <p className='text-sm'>
                                  Amount: ${benefit.benefitAmount}
                                </p>
                              )}
                              {benefit.benefitPercent && (
                                <p className='text-sm'>
                                  Percentage: {benefit.benefitPercent}%
                                </p>
                              )}
                              {benefit.benefitQuantity && (
                                <p className='text-sm'>
                                  Quantity: {benefit.benefitQuantity}
                                  {benefit.quantityQualifier &&
                                    ` (${benefit.quantityQualifier})`}
                                </p>
                              )}
                              {benefit.timeQualifier && (
                                <p className='text-sm'>
                                  Time Period: {benefit.timeQualifier}
                                </p>
                              )}
                              {benefit.authOrCertIndicator && (
                                <p className='text-sm text-orange-600'>
                                  Authorization Required:{' '}
                                  {benefit.authOrCertIndicator}
                                </p>
                              )}
                              {benefit.inPlanNetworkIndicator && (
                                <p className='text-sm'>
                                  In Network:{' '}
                                  {benefit.inPlanNetworkIndicator === 'Y'
                                    ? 'Yes'
                                    : 'No'}
                                </p>
                              )}
                              {benefit.timePeriodQualifier && (
                                <p className='text-sm'>
                                  Period Qualifier:{' '}
                                  {benefit.timePeriodQualifier}
                                </p>
                              )}
                              {benefit.compositeMedicalProcedureIdentifier && (
                                <p className='text-sm'>
                                  Medical Procedure:{' '}
                                  {JSON.stringify(
                                    benefit.compositeMedicalProcedureIdentifier
                                  )}
                                </p>
                              )}
                              {benefit.additionalInformation &&
                                benefit.additionalInformation.length > 0 && (
                                  <div className='text-sm'>
                                    <p className='font-medium'>
                                      Additional Information:
                                    </p>
                                    <ul className='list-disc list-inside ml-2'>
                                      {benefit.additionalInformation.map(
                                        (info, infoIdx) => (
                                          <li
                                            key={infoIdx}
                                            className='text-xs text-muted-foreground'
                                          >
                                            {JSON.stringify(info)}
                                          </li>
                                        )
                                      )}
                                    </ul>
                                  </div>
                                )}
                              {benefit.benefitsRelatedEntities &&
                                benefit.benefitsRelatedEntities.length > 0 && (
                                  <div className='text-sm'>
                                    <p className='font-medium'>
                                      Related Entities (COB):
                                    </p>
                                    <ul className='list-disc list-inside ml-2'>
                                      {benefit.benefitsRelatedEntities.map(
                                        (entity, entityIdx) => (
                                          <li
                                            key={entityIdx}
                                            className='text-xs text-muted-foreground'
                                          >
                                            {JSON.stringify(entity)}
                                          </li>
                                        )
                                      )}
                                    </ul>
                                  </div>
                                )}
                              {/* Code versions for technical details */}
                              {(benefit.coverageLevelCode ||
                                benefit.insuranceTypeCode ||
                                benefit.timeQualifierCode) && (
                                <details className='text-xs text-muted-foreground mt-2'>
                                  <summary className='cursor-pointer'>
                                    Technical Codes
                                  </summary>
                                  <div className='mt-1 space-y-1'>
                                    {benefit.coverageLevelCode && (
                                      <p>
                                        Coverage Level Code:{' '}
                                        {benefit.coverageLevelCode}
                                      </p>
                                    )}
                                    {benefit.insuranceTypeCode && (
                                      <p>
                                        Insurance Type Code:{' '}
                                        {benefit.insuranceTypeCode}
                                      </p>
                                    )}
                                    {benefit.timeQualifierCode && (
                                      <p>
                                        Time Qualifier Code:{' '}
                                        {benefit.timeQualifierCode}
                                      </p>
                                    )}
                                    {benefit.quantityQualifierCode && (
                                      <p>
                                        Quantity Qualifier Code:{' '}
                                        {benefit.quantityQualifierCode}
                                      </p>
                                    )}
                                    {benefit.inPlanNetworkIndicatorCode && (
                                      <p>
                                        Network Indicator Code:{' '}
                                        {benefit.inPlanNetworkIndicatorCode}
                                      </p>
                                    )}
                                  </div>
                                </details>
                              )}
                              {benefit.description && (
                                <p className='text-sm text-muted-foreground'>
                                  {benefit.description}
                                </p>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                  {/* Raw X12 Response */}
                  {response.x12 && (
                    <div>
                      <label className='block text-sm font-medium mb-2'>
                        Raw X12 271 Response
                      </label>
                      <Textarea
                        value={response.x12}
                        readOnly
                        className='font-mono text-xs'
                        rows={10}
                      />
                    </div>
                  )}

                  {/* Full JSON Response */}
                  <div>
                    <label className='block text-sm font-medium mb-2'>
                      Full JSON Response
                    </label>
                    <pre className='bg-muted p-4 rounded-md text-xs overflow-auto font-mono max-h-96'>
                      {JSON.stringify(response, null, 2)}
                    </pre>
                  </div>
                </>
              ) : (
                <div className='text-center py-12 text-muted-foreground'>
                  No response yet. Submit an eligibility check to see results.
                </div>
              )}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
};

export default Stedi_270_Testing;
